/**
 * Ficha de una anomalía.
 *
 * Antes sólo había listado: se veía que existía un problema pero no se podía
 * abrir. Para saber de qué equipo era o qué se propuso hacer había que llamar
 * al técnico, y para cambiarle el estado no había forma desde el escritorio.
 *
 * Dos decisiones:
 *
 * - **Sólo se ofrecen las transiciones que el dominio permite.** La máquina de
 *   estados vive en `canTransitionAnomaly` y la comprueban también el servicio
 *   y un disparador de la base; enseñar aquí botones que el servidor va a
 *   rechazar sería mentirle al usuario.
 * - **El historial va entero y visible.** Una anomalía dice lo que pasa hoy;
 *   su historial dice quién decidió qué y cuándo, que es lo que hace falta el
 *   día que un cliente discute una factura de reparación.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ANOMALY_STATUSES, canTransitionAnomaly, type AnomalyStatus } from '@fibwifire/domain/browser';
import { useSession } from '../../session/session-context.js';
import { ApiError } from '../../session/api-error.js';
import { DatumGroup, Datum, DetailShell, type Tab } from './detail-shell.js';
import { day, dateTime } from '../../table/format.js';

interface Anomalia {
  id: string;
  number: string;
  severity: string;
  status: string;
  anomaly_type: string | null;
  description: string;
  proposed_action: string | null;
  uncorrectable: boolean;
  out_of_service: boolean;
  source: string | null;
  detected_at: string | null;
  due_date: string | null;
  corrected_at: string | null;
  verified_at: string | null;
  closed_at: string | null;
  closure_notes: string | null;
  customer_id: string;
  customer_name: string;
  site_id: string;
  site_name: string;
  site_code: string | null;
  equipment_id: string | null;
  equipment_code: string | null;
  equipment_type: string | null;
  location_name: string | null;
  inspection_id: string | null;
  inspection_number: string | null;
  inspection_date: string | null;
  corrective_work_order_id: string | null;
  work_order_number: string | null;
  detected_by_name: string | null;
  verified_by_name: string | null;
}

interface Paso {
  to_status: string;
  reason: string | null;
  changed_at: string;
  changed_by_name: string | null;
}

const GRAVEDAD: Record<string, { label: string; tone: string; icon: string }> = {
  MUY_GRAVE: { label: 'Muy grave', tone: 'bad', icon: '⚠' },
  GRAVE: { label: 'Grave', tone: 'warn', icon: '▲' },
  LEVE: { label: 'Leve', tone: 'off', icon: '■' },
};

/** Cómo se lee cada estado. Los códigos son para la base, no para la gente. */
const ESTADO: Record<string, string> = {
  DETECTADA: 'Detectada',
  NOTIFICADA: 'Notificada al cliente',
  PENDIENTE_PRESUPUESTO: 'Pendiente de presupuesto',
  PRESUPUESTO_ENVIADO: 'Presupuesto enviado',
  ACEPTADA: 'Aceptada por el cliente',
  PENDIENTE_INTERVENCION: 'Pendiente de intervención',
  CORREGIDA: 'Corregida',
  VERIFICADA: 'Verificada',
  CERRADA: 'Cerrada',
  RECHAZADA_CLIENTE: 'Rechazada por el cliente',
};

export function AnomalyDetailScreen(): React.ReactNode {
  const { api } = useSession();
  const { id } = useParams<{ id: string }>();
  const [anomalia, setAnomalia] = useState<Anomalia | null>(null);
  const [historial, setHistorial] = useState<Paso[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cambiando, setCambiando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(() => {
    return api
      .get<{ anomaly: Anomalia; history: Paso[] }>(`/anomalies/${id}`)
      .then((d) => {
        setAnomalia(d.anomaly);
        setHistorial(d.history);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ApiError ? caught.message : 'No se ha podido cargar la anomalía.');
      })
      .finally(() => setCargando(false));
  }, [api, id]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cambiarEstado = useCallback(
    (destino: string) => {
      setCambiando(true);
      setAviso(null);
      api
        .post(`/anomalies/${id}/status`, { status: destino })
        .then(() => cargar())
        .catch((caught: unknown) => {
          setAviso(caught instanceof ApiError ? caught.message : 'No se ha podido cambiar el estado.');
        })
        .finally(() => setCambiando(false));
    },
    [api, id, cargar],
  );

  const g = GRAVEDAD[anomalia?.severity ?? ''] ?? { label: anomalia?.severity ?? '', tone: 'off', icon: '■' };

  // Sólo lo que el dominio admite desde el estado actual.
  const siguientes =
    anomalia === null
      ? []
      : ANOMALY_STATUSES.filter((s) =>
          canTransitionAnomaly(anomalia.status as AnomalyStatus, s as AnomalyStatus),
        );

  const tabs: Tab[] = [
    {
      id: 'resumen',
      label: 'Resumen',
      render: () => (
        <>
          <DatumGroup title="Qué pasa">
            <Datum label="Descripción">{anomalia?.description}</Datum>
            <Datum
              label="Qué hay que hacer"
              help={anomalia?.proposed_action ? undefined : 'Sin acción propuesta, el cliente no sabe qué se le está pidiendo que apruebe.'}
            >
              {anomalia?.proposed_action ?? '—'}
            </Datum>
            <Datum label="Gravedad">{g.label}</Datum>
            <Datum
              label="Fuera de servicio"
              help={anomalia?.out_of_service ? 'Un equipo fuera de servicio no protege la instalación.' : undefined}
            >
              {anomalia?.out_of_service ? 'Sí' : 'No'}
            </Datum>
            <Datum label="No corregible">{anomalia?.uncorrectable ? 'Sí, hay que sustituir el equipo' : 'No'}</Datum>
            <Datum label="Plazo">{anomalia?.due_date ? day(anomalia.due_date) : '—'}</Datum>
          </DatumGroup>

          <DatumGroup title="Dónde y de qué">
            <Datum label="Cliente">
              <Link to={`/clientes/${anomalia?.customer_id}`}>{anomalia?.customer_name}</Link>
            </Datum>
            <Datum label="Centro">
              <Link to={`/centros/${anomalia?.site_id}`}>{anomalia?.site_name}</Link>
            </Datum>
            <Datum label="Equipo">
              {anomalia?.equipment_id ? (
                <Link to={`/equipos/${anomalia.equipment_id}`}>{anomalia.equipment_code}</Link>
              ) : (
                '—'
              )}
            </Datum>
            <Datum label="Tipo">{anomalia?.equipment_type ?? '—'}</Datum>
            <Datum label="Ubicación">{anomalia?.location_name ?? '—'}</Datum>
            <Datum label="Revisión de origen">
              {anomalia?.inspection_id ? (
                <Link to={`/revisiones/${anomalia.inspection_id}`}>
                  {anomalia.inspection_number} · {day(anomalia.inspection_date)}
                </Link>
              ) : (
                '—'
              )}
            </Datum>
            <Datum label="Orden correctiva">{anomalia?.work_order_number ?? '—'}</Datum>
          </DatumGroup>

          <DatumGroup title="Quién y cuándo">
            <Datum label="Detectada">{dateTime(anomalia?.detected_at ?? null)}</Datum>
            <Datum label="Detectada por">{anomalia?.detected_by_name ?? '—'}</Datum>
            <Datum label="Corregida">{anomalia?.corrected_at ? dateTime(anomalia.corrected_at) : '—'}</Datum>
            <Datum label="Verificada">{anomalia?.verified_at ? dateTime(anomalia.verified_at) : '—'}</Datum>
            <Datum label="Verificada por">{anomalia?.verified_by_name ?? '—'}</Datum>
            <Datum label="Cerrada">{anomalia?.closed_at ? dateTime(anomalia.closed_at) : '—'}</Datum>
            <Datum label="Notas de cierre">{anomalia?.closure_notes ?? '—'}</Datum>
          </DatumGroup>
        </>
      ),
    },
    {
      id: 'historial',
      label: 'Historial',
      count: historial.length,
      render: () =>
        historial.length === 0 ? (
          <p className="empty">Sin movimientos registrados.</p>
        ) : (
          <ol className="timeline">
            {historial.map((paso, i) => (
              <li key={i}>
                <strong>{ESTADO[paso.to_status] ?? paso.to_status}</strong>
                <div className="hint">
                  {dateTime(paso.changed_at)}
                  {paso.changed_by_name ? ` · ${paso.changed_by_name}` : ''}
                  {paso.reason ? ` · ${paso.reason}` : ''}
                </div>
              </li>
            ))}
          </ol>
        ),
    },
  ];

  return (
    <DetailShell
      title={anomalia?.number ?? 'Anomalía'}
      subtitle={<Link to="/anomalias">← Anomalías</Link>}
      badges={
        anomalia === null ? null : (
          <>
            <span className={`pill pill--${g.tone}`}>{`${g.icon} ${g.label}`}</span>
            <span className="pill">{ESTADO[anomalia.status] ?? anomalia.status}</span>
            {anomalia.out_of_service && <span className="pill pill--bad">Fuera de servicio</span>}
          </>
        )
      }
      actions={
        anomalia === null ? null : (
          <>
            {siguientes.map((destino) => (
              <button
                key={destino}
                type="button"
                className="button button--quiet button--small"
                onClick={() => cambiarEstado(destino)}
                disabled={cambiando}
              >
                {ESTADO[destino] ?? destino}
              </button>
            ))}
            {siguientes.length === 0 && <span className="hint">Esta anomalía está cerrada.</span>}
            {aviso && <span className="hint">{aviso}</span>}
          </>
        )
      }
      tabs={tabs}
      loading={cargando}
      error={error}
    />
  );
}
